import { useCallback } from 'react';
import { useRegistration } from '@/context/RegistrationContext';
import { useAnalytics } from '@/hooks/useAnalytics';
import { redirect } from '@/navigation';
import { WREG_QR_LINK, useWregAppLink } from '@/components/webreg/useWregAppLink';
import { WregIntake } from '@/components/webreg/screens/WregIntake';
import { WregIntakeAbout } from '@/components/webreg/screens/WregIntakeAbout';
import { WregIntakeContact } from '@/components/webreg/screens/WregIntakeContact';
import { WregIntakeMedicare } from '@/components/webreg/screens/WregIntakeMedicare';
import { WregScreener } from '@/components/webreg/screens/WregScreener';
import { WregTracks } from '@/components/webreg/screens/WregTracks';
import { WregAgreement } from '@/components/webreg/screens/WregAgreement';
import { WregSwitch } from '@/components/webreg/screens/WregSwitch';
import { WregLabs } from '@/components/webreg/screens/WregLabs';
import { WregMonitor } from '@/components/webreg/screens/WregMonitor';
import { WregAllSet } from '@/components/webreg/screens/WregAllSet';
import { useWregFlowActions } from '@/components/webreg/WregFlowProvider';
import { useWregDev } from '@/components/webreg/dev/WregDevContext';
import { wregShippingFromContext } from '@/components/webreg/wregFlowState';
import type { WregScreenEvent } from '@/components/webreg/screens/wregScreenEvents';

const useScreenEvent = (beacon?: boolean): WregScreenEvent => {
  const { track } = useAnalytics();
  return useCallback(
    (event, properties) => track({ event, properties, beacon }),
    [beacon, track],
  );
};

const WregIntakeRoute = () => {
  const { form, intakeNotice, intakeNoticeStep, saveIntakeName } = useWregFlowActions();
  const onEvent = useScreenEvent();
  return (
    <WregIntake
      notice={intakeNoticeStep === 'intake' ? intakeNotice : null}
      initial={form}
      onContinue={saveIntakeName}
      onEvent={onEvent}
    />
  );
};

const WregIntakeContactRoute = () => {
  const { form, intakeNotice, intakeNoticeStep, saveIntakeContact } = useWregFlowActions();
  const onEvent = useScreenEvent();
  return (
    <WregIntakeContact
      notice={intakeNoticeStep === 'intake-contact' ? intakeNotice : null}
      initial={form}
      onContinue={saveIntakeContact}
      onEvent={onEvent}
    />
  );
};

const WregIntakeAboutRoute = () => {
  const { form, intakeNotice, intakeNoticeStep, saveIntakeAbout } = useWregFlowActions();
  const onEvent = useScreenEvent();
  // The only notice raised for this step is the eligibility date-of-birth rejection.
  return (
    <WregIntakeAbout
      dobError={intakeNoticeStep === 'intake-about' ? intakeNotice : null}
      initial={form}
      onContinue={saveIntakeAbout}
      onEvent={onEvent}
    />
  );
};

const WregIntakeMedicareRoute = () => {
  const { form, invite, promoError, intakeNotice, intakeNoticeStep, runIntake, afterIntake } =
    useWregFlowActions();
  const onEvent = useScreenEvent();
  return (
    <WregIntakeMedicare
      notice={intakeNoticeStep === 'intake-mbi' ? intakeNotice : null}
      initial={form}
      invite={invite}
      promoError={promoError}
      onSubmit={runIntake}
      onSubmitted={afterIntake}
      onEvent={onEvent}
    />
  );
};

const WregScreenerRoute = () => {
  const { onScreenerContinue, onScreenerIneligible } = useWregFlowActions();
  const onEvent = useScreenEvent();
  return (
    <WregScreener
      onContinue={onScreenerContinue}
      onIneligible={onScreenerIneligible}
      onEvent={onEvent}
    />
  );
};

const WregTracksRoute = () => {
  const { elig, tracks, tracksGate, onTracksContinue, onTracksNoCondition } = useWregFlowActions();
  const { disclosure } = useWregDev();
  return (
    <WregTracks
      offered={elig.tracks}
      blocked={elig.blocked}
      gate={tracksGate}
      disclosure={disclosure}
      initial={tracks}
      onContinue={onTracksContinue}
      onNoCondition={onTracksNoCondition}
    />
  );
};

const WregAgreementRoute = () => {
  const { runAlign, afterAlign, onDecline } = useWregFlowActions();
  return <WregAgreement onAccept={runAlign} onAccepted={afterAlign} onDecline={onDecline} />;
};

const WregSwitchRoute = () => {
  const { tracks, onSwitchAttest, onDecline } = useWregFlowActions();
  const onEvent = useScreenEvent();
  return (
    <WregSwitch tracks={tracks} onSwitch={onSwitchAttest} onDecline={onDecline} onEvent={onEvent} />
  );
};

const WregLabsRoute = () => {
  const { registrationData } = useRegistration();
  const { onLabsYes, onLabsNo } = useWregFlowActions();
  const onEvent = useScreenEvent();
  return (
    <WregLabs
      initial={registrationData.labsCompleted}
      onYes={onLabsYes}
      onNo={onLabsNo}
      onEvent={onEvent}
    />
  );
};

const WregMonitorRoute = () => {
  const { registrationData } = useRegistration();
  const { saveShipping, onShippingContinued } = useWregFlowActions();
  const onEvent = useScreenEvent();
  return (
    <WregMonitor
      initial={wregShippingFromContext(registrationData)}
      onContinue={saveShipping}
      onContinued={onShippingContinued}
      onEvent={onEvent}
    />
  );
};

const WregAllSetRoute = () => {
  const getAppLink = useWregAppLink();
  const onEvent = useScreenEvent(true);
  return (
    <WregAllSet
      onEnter={() => redirect(getAppLink())}
      onEvent={onEvent}
      qrLink={getAppLink(WREG_QR_LINK)}
    />
  );
};

export {
  WregAgreementRoute,
  WregAllSetRoute,
  WregIntakeAboutRoute,
  WregIntakeContactRoute,
  WregIntakeMedicareRoute,
  WregIntakeRoute,
  WregLabsRoute,
  WregMonitorRoute,
  WregScreenerRoute,
  WregSwitchRoute,
  WregTracksRoute,
};
